import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Clock, CheckCircle2, FolderKanban, Image, ArrowRight, Phone, Mail, MailCheck, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const STATUS_COLORS = {
  new: "bg-primary/10 text-primary",
  contacted: "bg-blue-100 text-blue-700",
  quoted: "bg-amber-100 text-amber-700",
  scheduled: "bg-purple-100 text-purple-700",
  completed: "bg-green-100 text-green-700",
  lost: "bg-muted text-muted-foreground",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    api.get("/leads/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/leads").then((r) => setRecent(r.data.slice(0, 6))).catch(() => {});
    api.get("/email/quota").then((r) => setQuota(r.data)).catch(() => {});
  }, []);

  const cards = [
    { label: "New Leads", value: stats?.new ?? 0, icon: Inbox, accent: true },
    { label: "In Progress", value: (stats?.contacted ?? 0) + (stats?.quoted ?? 0) + (stats?.scheduled ?? 0), icon: Clock },
    { label: "Completed", value: stats?.completed ?? 0, icon: CheckCircle2 },
    { label: "Active Projects", value: stats?.projects ?? 0, icon: FolderKanban },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl font-extrabold">Dashboard</h1>
        <p className="text-muted-foreground">Overview of leads and activity.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className={`p-6 border ${c.accent ? "bg-primary text-white border-primary" : "bg-card border-border"}`} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <c.icon className={`h-6 w-6 ${c.accent ? "text-white" : "text-primary"}`} />
            <div className="font-heading text-4xl font-extrabold mt-4">{c.value}</div>
            <div className={`text-sm mt-1 ${c.accent ? "text-white/80" : "text-muted-foreground"}`}>{c.label}</div>
          </div>
        ))}
      </div>

      {quota && (
        <div className={`border p-5 ${quota.low ? "border-red-300 bg-red-50" : "border-border bg-card"}`} data-testid="email-quota-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              {quota.low ? <AlertTriangle className="h-5 w-5 text-red-600" /> : <MailCheck className="h-5 w-5 text-primary" />}
              <div>
                <div className="font-bold">Daily Email Quota {quota.low && <span className="text-red-600">— running low!</span>}</div>
                <div className="text-sm text-muted-foreground">
                  <span data-testid="quota-remaining" className={quota.low ? "font-bold text-red-600" : "font-bold text-foreground"}>{quota.remaining}</span> of {quota.limit} emails remaining today · resets midnight UTC
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-heading text-2xl font-extrabold">{quota.sent}<span className="text-muted-foreground text-base font-bold">/{quota.limit}</span></div>
              <div className="text-xs text-muted-foreground">sent today</div>
            </div>
          </div>
          <div className="mt-3 h-2 w-full bg-muted rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${quota.low ? "bg-red-500" : "bg-primary"}`} style={{ width: `${Math.min((quota.sent / quota.limit) * 100, 100)}%` }} />
          </div>
        </div>
      )}

      <div className="bg-card border border-border">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="font-heading text-xl font-bold">Recent Leads</h2>
          <Link to="/admin/leads" className="text-sm font-bold text-primary flex items-center gap-1">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {recent.length === 0 ? (
          <p className="p-8 text-center text-muted-foreground">No leads yet. They'll appear here when customers submit the quote form.</p>
        ) : (
          <div className="divide-y divide-border">
            {recent.map((l) => (
              <Link to="/admin/leads" key={l.id} className="flex items-center justify-between p-5 hover:bg-muted/50 transition-colors" data-testid={`recent-lead-${l.id}`}>
                <div className="min-w-0">
                  <div className="font-bold">{l.name}</div>
                  <div className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 mt-0.5">
                    <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {l.phone}</span>
                    <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {l.email}</span>
                    <span>{l.service_type}</span>
                  </div>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 shrink-0 ${STATUS_COLORS[l.status]}`}>{l.status.toUpperCase()}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
